import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIMES } from '../../shared/providers/react-query-provider';
import { useGetLoggedAgent } from '../agent/hooks';

import { fetchAgencyCapacityFillingStatus, fetchAgentWorkload, getAgentAwayStatus } from './api';
import { AgencyCapacityFillingStatus, AgentWorkload } from './types';
import { getWorkloadNeverConfiguredSince, setWorkloadNeverConfiguredSince } from './utils';
import { logQueryError } from './logging';

/**
 * Query key factory for agent away status
 */
export const getAgentAwayStatusQueryKey = () => ['agentSettings', 'awayStatus'] as const;

/**
 * Query key factory for agent workload
 */
export const getAgentWorkloadQueryKey = () => ['agentSettings', 'workload'] as const;

/**
 * Query key factory for agency capacity filling status
 */
export const getAgencyCapacityFillingStatusQueryKey = () =>
    ['agentSettings', 'agencyCapacityFillingStatus'] as const;

/**
 * Hook to fetch the agent away status
 * @returns Query result with boolean indicating if agent is away
 */
export const useGetAgentAwayStatus = () => {
    const agent = useGetLoggedAgent();

    return useQuery<boolean>({
        queryKey: getAgentAwayStatusQueryKey(),
        queryFn: async () => {
            try {
                return await getAgentAwayStatus();
            } catch (error) {
                logQueryError('fetch agent away status', error, {}, agent);
                throw error;
            }
        },
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

/**
 * Hook to fetch the agent workload
 * @returns Query result with AgentWorkload data
 */
export const useGetAgentWorkload = () => {
    const agent = useGetLoggedAgent();

    return useQuery<AgentWorkload>({
        queryKey: getAgentWorkloadQueryKey(),
        queryFn: async () => {
            try {
                return await fetchAgentWorkload();
            } catch (error) {
                logQueryError(
                    'fetch agent workload',
                    error,
                    {
                        workloadNeverConfiguredSince: getWorkloadNeverConfiguredSince(),
                    },
                    agent,
                );
                throw error;
            }
        },
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (failureCount) => {
            // On first failure, store in localStorage for tracking
            if (failureCount === 0 && !getWorkloadNeverConfiguredSince()) {
                setWorkloadNeverConfiguredSince(new Date());
            }
            // Retry up to 2 times
            return failureCount < 2;
        },
    });
};

/**
 * Hook to fetch the agency capacity filling status for next two months
 * @returns Query result with AgencyCapacityFillingStatus data
 */
export const useGetAgencyCapacityFillingStatus = () => {
    const agent = useGetLoggedAgent();

    return useQuery<AgencyCapacityFillingStatus>({
        queryKey: getAgencyCapacityFillingStatusQueryKey(),
        queryFn: async () => {
            try {
                return await fetchAgencyCapacityFillingStatus();
            } catch (error) {
                logQueryError('fetch agency capacity filling status', error, {}, agent);
                throw error;
            }
        },
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
